import { FormControl, InputLabel, MenuItem, Select } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import moment from 'moment';
import React, { useMemo } from 'react';

const LABEL_BY_PERIOD = {
  day: 'Date',
  week: 'Week',
  month: 'Month',
  year: 'Year',
};

function buildOptions(period) {
  if (period === 'week') {
    const lastSunday = moment().subtract(1, 'day').startOf('week');
    return Array.from({ length: 100 }, (_, i) => {
      const endDate = lastSunday.clone().subtract(i, 'weeks');
      return {
        value: endDate.format('YYYY-MM-DD'),
        label: `${endDate.clone().subtract(6, 'days').format('L')} - ${endDate.format('L')}`,
      };
    });
  }
  if (period === 'month') {
    const thisMonth = moment().subtract(1, 'month').endOf('month');
    return Array.from({ length: 36 }, (_, i) => {
      const endDate = thisMonth.clone().subtract(i, 'month').endOf('month');
      return { value: endDate.format('YYYY-MM-DD'), label: endDate.format('MMM YYYY') };
    });
  }
  if (period === 'year') {
    const lastYear = moment().subtract(1, 'year').endOf('year');
    const firstYear = moment('2012-01-01').endOf('year');
    const years = lastYear.diff(firstYear, 'years') + 1;
    return Array.from({ length: years }, (_, i) => {
      const endDate = lastYear.clone().subtract(i, 'year').endOf('year');
      return { value: endDate.format('YYYY-MM-DD'), label: endDate.format('YYYY') };
    });
  }
  return [];
}

/**
 * Unified date selector for solar reports.
 *
 * - period="day" renders a DatePicker (free-form date pick)
 * - period="week"|"month"|"year" renders a Select of pre-computed periods
 *
 * `value`/`onChange` always speak in "YYYY-MM-DD" strings — the same
 * format the report API expects.
 */
function SolarDateSelector({ period, value, onChange, disabled }) {
  const options = useMemo(() => buildOptions(period), [period]);
  const label = LABEL_BY_PERIOD[period] || 'Date';

  if (period === 'day') {
    const dateValue = value ? moment(value).toDate() : null;
    return (
      <DatePicker
        label={label}
        value={dateValue}
        disabled={disabled}
        onChange={next => onChange(next ? moment(next).format('YYYY-MM-DD') : null)}
        slotProps={{ textField: { size: 'small' } }}
      />
    );
  }

  return (
    <FormControl size="small" sx={{ minWidth: 180 }}>
      <InputLabel id={`solar-date-${period}-label`}>{label}</InputLabel>
      <Select
        labelId={`solar-date-${period}-label`}
        label={label}
        size="small"
        value={value || ''}
        disabled={disabled}
        onChange={e => onChange(e.target.value)}
      >
        {options.map(opt => (
          <MenuItem key={opt.value} value={opt.value}>
            {opt.label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

export default SolarDateSelector;
